package com.bikompanion.data.parking

import com.bikompanion.domain.BikeParkingSpot
import com.bikompanion.domain.PARKING_CELL_DEGREES
import com.bikompanion.domain.ParkingCell
import com.bikompanion.domain.ParkingType

/** "APRK", so a truncated or wrong file is rejected rather than decoded into nonsense. */
private const val Magic = "APRK"
private const val SupportedVersion = 1

/** magic(4) + version(1) + cellMilliDegrees(2) + generatedAtEpochDay(4) + cellCount(4). */
private const val HeaderBytes = 15

/** key(4) + offset(4) + count(4), one per cell holding at least one rack, sorted by key. */
private const val IndexEntryBytes = 12

/** A position is stored as a fraction of its cell in this many steps — about 0.17 m. */
private const val PositionSteps = 65536

/** Longitude indices span -1800..1799, so this is what separates one latitude row from the next. */
private const val LongitudeIndexRange = 3600

/**
 * Every bike rack in OpenStreetMap, read out of the file the app ships.
 *
 * Bundled rather than fetched because the alternative was Overpass, and Overpass cannot be made
 * reliable from a phone: it is volunteer infrastructure, its own wiki calls the main instance
 * "currently overloaded" and asks a regular app to stay under about a hundred queries a day, and
 * on a given afternoon half the public mirrors are simply down — measured, two of four. No amount
 * of retrying, hedging or backing off fixes a service that is not there.
 *
 * The whole world is 926,356 racks and 5.5 MB, which is small enough that fetching it at all was
 * the wrong idea. Lookups are local, instant, and work on a plane.
 *
 * The cost is that the data is as old as the last release. Racks are street furniture — a council
 * installs one and it stays for years — so a few months of drift is invisible to a rider, and it
 * beats an empty map every time.
 *
 * Random access rather than one big decode: the file is held as bytes and only the cell being
 * drawn is turned into objects, so the memory cost is the 5.5 MB and a screenful of racks, not a
 * million allocations.
 */
internal class BundledParkingDataset private constructor(
    private val bytes: ByteArray,
    private val cellCount: Int,
    /** Days since the epoch, for saying how old the racks are. */
    val generatedAtEpochDay: Int,
) {
    /**
     * The racks inside [cell], or an empty list where there are none.
     *
     * Empty is a real answer here, unlike with Overpass: the file covers the whole planet, so a
     * cell that isn't in the index is ground genuinely without a mapped rack rather than a
     * request that failed.
     */
    fun racksIn(cell: ParkingCell): List<BikeParkingSpot> {
        val entry = indexOf(cellKey(cell)) ?: return emptyList()
        var at = HeaderBytes + cellCount * IndexEntryBytes + readInt(entry + 4)
        val count = readInt(entry + 8)
        val south = cell.latitudeIndex * PARKING_CELL_DEGREES
        val west = cell.longitudeIndex * PARKING_CELL_DEGREES
        return buildList(count) {
            repeat(count) {
                val latitudeOffset = readShort(at)
                val longitudeOffset = readShort(at + 2)
                val flags = bytes[at + 4].toInt() and 0xFF
                at += 5
                val capacity = if (flags and HasCapacity != 0) bytes[at++].toInt() and 0xFF else null
                add(
                    BikeParkingSpot(
                        // Position in the file is the only identity a rack has here — OSM ids are
                        // not stored, because nothing in the app looks a rack up by one and they
                        // would cost more than the coordinates do.
                        id = "${cell.latitudeIndex}/${cell.longitudeIndex}/$it",
                        latitude = south + latitudeOffset * StepDegrees,
                        longitude = west + longitudeOffset * StepDegrees,
                        capacity = capacity,
                        isCovered = when (flags and CoveredMask) {
                            CoveredYes -> true
                            CoveredNo -> false
                            else -> null
                        },
                        // Bits a file built before this shipped never set, so an old file reads
                        // every rack as Other rather than failing to open at all. Coerced rather
                        // than trusted outright: the header is validated on open, but a single
                        // corrupt record byte should not be able to index past the enum.
                        type = ParkingType.entries[
                            ((flags and TypeMask) ushr TypeShift).coerceAtMost(ParkingType.entries.lastIndex),
                        ],
                    ),
                )
            }
        }
    }

    /** Where [key]'s index entry starts, or null if that cell holds no racks. */
    private fun indexOf(key: Int): Int? {
        var low = 0
        var high = cellCount - 1
        while (low <= high) {
            val middle = (low + high) ushr 1
            val at = HeaderBytes + middle * IndexEntryBytes
            val found = readInt(at)
            when {
                found < key -> low = middle + 1
                found > key -> high = middle - 1
                else -> return at
            }
        }
        return null
    }

    private fun readInt(at: Int): Int =
        (bytes[at].toInt() and 0xFF shl 24) or
            (bytes[at + 1].toInt() and 0xFF shl 16) or
            (bytes[at + 2].toInt() and 0xFF shl 8) or
            (bytes[at + 3].toInt() and 0xFF)

    private fun readShort(at: Int): Int = (bytes[at].toInt() and 0xFF shl 8) or (bytes[at + 1].toInt() and 0xFF)

    companion object {
        private const val CoveredMask = 3
        private const val CoveredYes = 1
        private const val CoveredNo = 2
        private const val HasCapacity = 4
        private const val TypeShift = 3
        private const val TypeMask = 0b111 shl TypeShift

        private const val StepDegrees = PARKING_CELL_DEGREES / PositionSteps

        /**
         * Reads the header and checks it describes a file this build can use.
         *
         * Returns null rather than throwing on anything unexpected. A dataset that won't open is
         * a reason to fall back to the network, not to take the app down — and the checks are
         * what make that fallback reachable instead of a crash in the middle of a map draw.
         */
        fun openOrNull(bytes: ByteArray): BundledParkingDataset? {
            if (bytes.size < HeaderBytes) return null
            if (Magic.indices.any { bytes[it].toInt() != Magic[it].code }) return null
            if (bytes[4].toInt() != SupportedVersion) return null
            val cellMilliDegrees = (bytes[5].toInt() and 0xFF shl 8) or (bytes[6].toInt() and 0xFF)
            // The grid the file was built on has to be the grid the app asks in, or every lookup
            // would silently address the wrong ground.
            if (cellMilliDegrees != (PARKING_CELL_DEGREES * 1000).toInt()) return null
            val header = BundledParkingDataset(bytes, cellCount = 0, generatedAtEpochDay = 0)
            val generatedAtEpochDay = header.readInt(7)
            val cellCount = header.readInt(11)
            if (cellCount < 0 || bytes.size < HeaderBytes + cellCount.toLong() * IndexEntryBytes) return null
            return BundledParkingDataset(bytes, cellCount, generatedAtEpochDay)
        }

        /** Must match `key()` in tools/parking-dataset/BuildParkingDataset.java. */
        internal fun cellKey(cell: ParkingCell): Int = (cell.latitudeIndex + 900) * LongitudeIndexRange + (cell.longitudeIndex + 1800)
    }
}
